import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Alert from 'shared/atoms/Alert';
import Autocomplete from 'shared/atoms/Autocomplete';
import Dialog from 'shared/molecules/Dialog';
import Form from 'shared/atoms/Form';
import RadioGroup from 'shared/atoms/RadioGroup';
import Tooltip from 'shared/atoms/Tooltip';
import T from 'shared/atoms/Typography';
import ConfirmationDialog from 'shared/molecules/ConfirmationDialog';

import { actionsOnContentOwnership } from 'shared/const';
import theme from 'shared/themes/default';

import {
  AutoCompleteWrapper,
  ContentOwnershipWrapper,
  ContentOwnershipHeader,
  StyledErrorOutlineIcon,
  ContentOwnershipSubHeader,
  TransferContentOptionWrapper,
  SearchManagerWrapper,
  DeleteMembershipAlertText,
} from './styles';

import { useOrganizationMembers } from '../../context';

const ManageContentOwnershipDialog = () => {
  const {
    formStatus,
    groups: { groupsInfo },
    onCloseDelete,
    organizations: { getOrganizationManagers },
    registeredMembers,
    playlists: { playlists },
    users: {
      manageContentOwnership,
      onSubmitManageContentOwnership,
      transferContentChoiceOptions,
      transferContentOwnershipFor,
    },
  } = useOrganizationMembers();

  const managers = getOrganizationManagers()
    .sort((a, b) => {
      return a.name?.localeCompare(b.name);
    })
    .filter((user) => user.id !== transferContentOwnershipFor?.id);

  const [transferTo, setTransferTo] = useState(null);
  const [selectedTransferOption, setSelectedTransferOption] = useState();
  const [transferOwnership, setTransferOwnership] = useState(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmationDialogue, setShowConfirmationDialogue] =
    useState(false);

  const { t } = useTranslation();

  useEffect(() => {
    setSubmitting(false);
  }, [formStatus]);

  useEffect(() => {
    if (transferContentChoiceOptions.length > 0) {
      transferContentChoiceOptions.forEach((item) => {
        if (item?.checked) {
          setSelectedTransferOption(item.value);
          if (item.value === actionsOnContentOwnership.transfer) {
            setTransferOwnership(true);
          }
        }
      });
    }
  }, [transferContentChoiceOptions]);

  const onChangeContentTransferOption = (selectedOption) => {
    setTransferTo();
    if (selectedOption === actionsOnContentOwnership.transfer) {
      setTransferOwnership(true);
    } else {
      setTransferOwnership(false);
    }

    setSelectedTransferOption(selectedOption);
    setIsSubmitDisabled(selectedOption === actionsOnContentOwnership.transfer);
  };

  const onSelectManagerHandler = (manager) => {
    if (!manager) {
      setTransferTo(null);
      setIsSubmitDisabled(true);
      return;
    }

    setTransferTo(manager);
    setIsSubmitDisabled(false);
  };

  const onClose = () => {
    onCloseDelete(false);
  };

  const onSubmit = () => {
    if (selectedTransferOption || transferTo) {
      setShowConfirmationDialogue(true);
    }
  };

  const onConfirmHandler = () => {
    const ownGroups =
      groupsInfo.filter(
        (group) => group.groupOwner.id === transferContentOwnershipFor.id
      ).length > 0;
    const ownPlaylists =
      playlists.filter(
        (playlist) => playlist.ownerId === transferContentOwnershipFor.id
      ).length > 0;
    const memberId = registeredMembers.find(
      (member) => member.user.id === transferContentOwnershipFor.id
    ).id;

    setShowConfirmationDialogue(false);
    setSubmitting(true);
    onSubmitManageContentOwnership({
      actionOnContent: selectedTransferOption,
      contentTransferTo: transferTo?.id,
      groups: ownGroups,
      memberId,
      playlists: ownPlaylists,
    });
  };

  const submitButtonText =
    (selectedTransferOption === actionsOnContentOwnership.transfer &&
      t('administrateUserRole.deleteAccessAndTransferOwnership')) ||
    (selectedTransferOption === actionsOnContentOwnership.delete &&
      t('administrateUserRole.deleteAccessAndContent'));
  const confirmationDialogDescription =
    (selectedTransferOption === actionsOnContentOwnership.transfer &&
      t('administrateUserRole.confirmationDialogTransferAccessDescription')) ||
    (selectedTransferOption === actionsOnContentOwnership.delete &&
      t('administrateUserRole.confirmationDialogDeleteAccessDescription'));
  const confirmationButtonTitle =
    (selectedTransferOption === actionsOnContentOwnership.transfer &&
      t('administrateUserRole.transferAll')) ||
    (selectedTransferOption === actionsOnContentOwnership.delete &&
      t('administrateUserRole.deleteAll'));
  const actions = [
    {
      ariaLabel: submitButtonText,
      disabled: isSubmitDisabled,
      handler: onSubmit,
      label: submitButtonText,
      submit: true,
    },
  ];

  const hasActions = !formStatus?.success && managers.length > 0;
  return (
    <Dialog
      open={manageContentOwnership}
      onClose={onClose}
      ariaLabel={t('administrateUserRole.deleteAccessDialogTitle')}
      title={t('administrateUserRole.deleteAccessDialogTitle')}
      loading={submitting}
      actions={hasActions ? actions : null}
    >
      <>
        {showConfirmationDialogue && (
          <ConfirmationDialog
            open={showConfirmationDialogue}
            title={t('administrateUserRole.confirmationDialogTitle')}
            ariaLabel={t('administrateUserRole.confirmationDialogTitle')}
            description={confirmationDialogDescription}
            cancelText={t('generic.cancel')}
            confirmText={confirmationButtonTitle}
            onConfirm={onConfirmHandler}
            onCancel={() => setShowConfirmationDialogue(false)}
            onClose={() => setShowConfirmationDialogue(false)}
          />
        )}
        {!formStatus && (
          <>
            <T color={theme.palette.pureDark}>
              {t('administrateUserRole.deleteAccessDialogDescription')}
            </T>
            <ContentOwnershipWrapper>
              <ContentOwnershipHeader>
                {t('administrateUserRole.contentOwnershipHeader')}
                <Tooltip
                  title={t('administrateUserRole.contentOwnershipHelp')}
                  arrow
                >
                  <StyledErrorOutlineIcon />
                </Tooltip>
              </ContentOwnershipHeader>
              <ContentOwnershipSubHeader>
                {t('administrateUserRole.contentOwnershipSubHeader')}
              </ContentOwnershipSubHeader>

              <Form onSubmit={onSubmit}>
                <TransferContentOptionWrapper>
                  <RadioGroup
                    options={transferContentChoiceOptions}
                    onChange={onChangeContentTransferOption}
                    theme="light"
                  />
                </TransferContentOptionWrapper>
                {transferOwnership && (
                  <SearchManagerWrapper>
                    <AutoCompleteWrapper>
                      <Autocomplete
                        filterOptions
                        getOptionLabel={(option) =>
                          (option.name || '').toString()
                        }
                        noOptionsText={t('generic.noSearchResults')}
                        onChange={onSelectManagerHandler}
                        options={managers}
                        placeholder={t(
                          'administrateUserRole.searchPlaceHolder'
                        )}
                      />
                    </AutoCompleteWrapper>
                  </SearchManagerWrapper>
                )}
                <div>
                  <DeleteMembershipAlertText>
                    {t('administrateUserRole.deleteAccessDialogAlertText')}
                  </DeleteMembershipAlertText>
                </div>
              </Form>
            </ContentOwnershipWrapper>
          </>
        )}
        {formStatus && (
          <Alert
            message={formStatus.message}
            severity={formStatus.success ? 'success' : 'error'}
            theme="light"
          />
        )}
      </>
    </Dialog>
  );
};

export default ManageContentOwnershipDialog;
